import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate, useLoaderData, useLocation } from 'react-router-dom';
import { Tooltip, Container, Grid, Card, CardContent, Typography, Box, Avatar, TextField, Button, Input, MenuItem, Select, InputLabel, FormHelperText, FormControl, Snackbar, Alert, IconButton, Chip, Skeleton } from '@mui/material';
import { styled } from '@mui/system';
import { Edit, Save, AccountBalance, School, FileUpload, Email, Phone, Info, Language, Home, CalendarToday, LocationOn, Person, Lock, Delete, CheckCircle, Facebook, Instagram, Twitter, LinkedIn, YouTube, X } from '@mui/icons-material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import Header1 from '../Header/Header1';
import Header3 from '../Header/Header3';
import Footer from '../Footer/Footer';
import { Link as MuiLink } from '@mui/material';
import GenderIcon from '@mui/icons-material/Male';
import { Link } from 'react-router-dom';
import Footer2 from '../Footer/Footer2';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import VerificationForm from './VerificationForm';
import VerificationButton from './VerificationButton';


const API_URL = process.env.REACT_APP_API_URL;




const theme = createTheme({
  palette: {
    primary: {
      main: '#1111',
    },
    secondary: {
      main: '#1111',
    },
    background: {
      paper: '#1111',
    },
  },
});


const ProfileHeader = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #55555, #77777)',
  color: 'white',
  padding: theme.spacing(4),
  borderRadius: '10px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: theme.spacing(4),
  boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
}));

const ProfileCard = styled(Card)(({ theme }) => ({
  borderRadius: '15px',
  boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
  color: 'white',
  padding: theme.spacing(4),
  backgroundColor: '#1111',
  marginBottom: theme.spacing(10),
  borderColor: '#5555',
  "&:hover": {
    transform: "translateY(-3px)",
    borderColor: '#1E90FF',
    boxShadow: "0 10px 20px rgba(0, 0, 0, 0.15)",
  },
}));

const ProfileAvatar = styled(Avatar)(({ theme }) => ({
  width: theme.spacing(40),
  height: theme.spacing(40),
  borderRadius: '50%',
  border: `4px solid ${theme.palette.primary.main}`,
  marginRight: theme.spacing(4),
  borderColor: '#5555',
  "&:hover": {
    transform: "translateY(-3px)",
    borderColor: '#1E90FF',
    boxShadow: "0 10px 20px rgba(0, 0, 0, 0.15)",
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  letterSpacing: 3,
  fontSize: '1.1rem',
  marginBottom: theme.spacing(2),

}));

const InputField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  width: '100%',
  letterSpacing: 4,
  '& .MuiInputBase-root': {
    backgroundColor: '#5555',
    borderRadius: '10px',
    color: 'white',
  },
  '& .MuiInputBase-input': {
    color: 'white',
  },
  '& .MuiInputLabel-root': {
    color: 'white',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: 'white',
  },
  '& .MuiInputBase-input': {
    letterSpacing: '3px', // 👈 Applies spacing to the typed text
    color: 'white',
  },
  '& .MuiInputLabel-root': {
    letterSpacing: '2px', // 👈 Optional: applies spacing to the label
    color: 'white',
  },
}));


const DarkForm = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#2222',
  padding: '24px',
  letterSpacing: 3,
  borderRadius: '8px',
  boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.4)',
  width: '100%',
  maxWidth: '500px',
  margin: 'auto',
}));

const DarkFormControl = styled(FormControl)(({ theme }) => ({
  width: '100%',
  letterSpacing: 3,
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#2222',
    color: 'white',
    '& fieldset': {
      borderColor: '#2222',
    },
    '&:hover fieldset': {
      borderColor: '#3333',
    },
  },
}));

const DarkMenuItem = styled(MenuItem)(({ theme }) => ({
  backgroundColor: '#7777',
  letterSpacing: 3,
  color: 'white',
  '&:hover': {
    backgroundColor: '#444',
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: 2, // Applying border-radius to MenuItem (if applicable)
  },
  '& .MuiInputLabel-root': {
    color: 'white', // Making label color white
  },
}));

const DarkSelect = styled(Select)(({ theme }) => ({
  color: 'white',
  letterSpacing: 3,
  '& .MuiSelect-select': {
    padding: theme.spacing(2),
    backgroundColor: '#5555',
    color: 'white',
    borderRadius: '10px',
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: 2, // Applying border-radius to input (for Select)
  },
  '& .MuiInputLabel-root': {
    color: 'white', // Making label color white
  },
  '&.MuiInputLabel-shrink': {
    color: 'white',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: 'white',
  }

}));

const ActionButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#2222',
  letterSpacing: 3,
  color: 'black',
  padding: theme.spacing(1, 4),
  borderRadius: '25px',
  '&:hover': {
    backgroundColor: '#1E90FF',
  },
  marginTop: theme.spacing(2),
}));


function UserProfile({ onLogout }) {


  useEffect(() => {
    window.scrollTo(0, 0);  // Scroll to top when this page is loaded
  }, []);


  const { userId } = useParams();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };


  const [language, setLanguage] = useState('en');
  const [newPassword, setNewPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [userStats, setUserStats] = useState({
    totalEarnings: 0,
    postsCount: 0,
    totalBookings: 0,
  });


  const navigate = useNavigate();


  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [userData, setUserData] = useState({
    "name": "",
    "about": "",
    "contact": "",
    "address": "",
    "created_at": "",
    "dob": "",
    "email": "",
    "gender": "",
    "id": "",
    "image": "",
    "languages": "",
    "password": "",
    "rating": "",
    "role": "",
    "status": "",
    "surname": "",
    "city": "",
    "state": "",
    "country": "",
    "pincode": "",
    "updated_at": "",
    "facebook": "",
    "instagram": "",
    "x": "",
    "linkedin": "",
    "youtube": "",
    "currentLevel": "Advanced",
    "totalTimeLearning": "150 hrs",
    "totalTimeTeaching": "50 hrs",
    "totalSessions": "25",
    "ratings": "4.8/5",
  });

  const [image, setImage] = useState(null);
  const [error, setError] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');

  const handleImageChange = (event) => {
    const file = event.target.files[0];

    if (file) {
      if (file.size > 250 * 1024) {  // 250 KB
        setError('File size exceeds the 250KB limit.');
        setImage(null);
        setPreviewUrl('');  // Clear preview
      } else {
        setError('');
        setImage(file);
        setPreviewUrl(URL.createObjectURL(file));  // Show preview
      }
    }
  };



  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!image) {
      setError('Please select an image.');
      return;
    }

    if (image.size > 250 * 1024) {
      setError('File size exceeds 250KB.');
      return;
    }

    const formData = new FormData();
    formData.append('profilePicture', image);
    formData.append('userId', userId);

    try {
      const response = await axios.patch(`${API_URL}/update_profile_picture/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      alert('Profile picture updated successfully.');
      window.location.reload();
    } catch (error) {
      console.error('Error updating profile picture:', error);
      const msg = error.response?.data?.error || 'Error uploading image.';
      setError(msg);
    }
  };






  useEffect(() => {
    if (!userId) {
      return;
    }

    axios
      .get(`${API_URL}/get_user_data/?user_id=${userId}`)
      .then((response) => {
        setUserData(response.data);
      })
      .catch((err) => {
        console.error(err);
      });
  }, [userId]);


  const extractLanguages = (data) => {
    const allLanguages = [];

    data.forEach(item => {
      allLanguages.push({
        id: item.id,   // Use 'id' as the value
        name: item.name,  // Display the 'name' in the dropdown
      });
    });

    setLanguages(allLanguages);  // Set languages as an array of objects
  };

  useEffect(() => {
    const fetchSections = async () => {
      try {
        const response = await fetch(`${API_URL}/list_sections_and_subsections/`);
        const data = await response.json();
        extractLanguages(data.languages);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchSections();
  }, []);


  useEffect(() => {
    const fetchUserStats = async () => {
      try {
        // Make the API call
        const response = await axios.get(`${API_URL}/get_user_stats/?user_id=${userId}`);

        // Set the response data in the state
        setUserStats({
          totalEarnings: response.data.total_earnings,
          totalPosts: response.data.posts_count,
          totalBookings: response.data.total_bookings_made,
          paybleAmount: response.data.payable_amount
        });
      } catch (err) {
        setError('Error fetching user stats');
      }
    };

    fetchUserStats();
  }, []);


  const handleLanguageChange = (e) => {
    setUserData({
      ...userData,
      language: e.target.value
    });
  };



  const handleLogout = () => {
    onLogout();
    navigate('/login');
  };



  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      alert('New passwords do not match.');
      return;
    }

    try {
      const payload = { userId, newPassword };

      if (userData.password) {
        payload.currentPassword = currentPassword;
      }

      await axios.patch(`${API_URL}/change_user_password/`, payload);
      alert('Password changed successfully.');
      setShowChangePassword(false);
    } catch (error) {
      console.error('Error changing password:', error);
      alert('There was an error changing your password. Please try again later.');
    }
  };



  const [openSnackbar, setOpenSnackbar] = useState(false);


  const handleChange = (e, name) => {
    setUserData({
      ...userData,
      [name]: e.target.value
    });
  };


  const [selectedSection, setSelectedSection] = useState('');
  const [selectedSubSection, setSelectedSubSection] = useState('');

  const [isEditing, setIsEditing] = useState(false);


  const toggleEditMode = async () => {
    if (isEditing) {
      try {
        const updatedUserData = {
          ...userData,
          user_id: userId // Ensure this matches the backend expectation
        };

        console.log('user_data', userData)

        const response = await axios.put(`${API_URL}/update_users/`, updatedUserData, {
          headers: {
            'Content-Type': 'application/json'
          }
        });

        alert('Profile updated successfully!');
      } catch (error) {
        console.error('Error updating profile:', error);
        alert('Failed to update profile. Please try again.');
      }
    }
    setIsEditing(!isEditing);
  };




  const handleDeleteAccount = async () => {
    if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      try {
        await axios.patch(`${API_URL}/deactivate_user/${userId}/`);
        alert('Account deactivated successfully.');
        onLogout();
      } catch (error) {
        console.error('Error deactivating account:', error);
        alert('There was an error deactivating your account. Please try again later.');
      }
    }
  };

  const specializedFields = userData?.specialized_fields || [];
  const roles = userData?.roles || [];



  return (
    <div>
      <Header1 userId={userId} onLogout={onLogout} />

      <Typography
        variant="h3"
        component="h1"
        align="center"
        fontSize={{ xs: '2rem', sm: '2.5rem', md: '3rem' }}
        sx={{
          backgroundImage: 'linear-gradient(45deg, #00bfff, #ff00ff)',
          mt: 10,
          letterSpacing: 7,
          WebkitBackgroundClip: 'text',  // This makes the gradient clip to the text
          color: 'transparent',  // Makes the text itself transparent to show the gradient
        }}
        gutterBottom
      >
        User Portal
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexWrap: 'wrap',
          minHeight: '100vh',
          backgroundColor: 'black',
          padding: 2,  // Add padding for spacing
          gap: 2,      // Add space between items in the column
          justifyContent: 'flex-start',
          alignItems: 'center',  // Center items horizontally in smaller screens
          width: '100%',  // Make sure it spans the full width of the screen
          '@media (min-width: 600px)': {
            flexDirection: 'row', // On larger screens, switch to row layout
            justifyContent: 'space-evenly', // Distribute space evenly between items
            alignItems: 'center', // Align items centrally along the row
          },
          '@media (min-width: 1024px)': {
            padding: 4, // Increase padding on larger screens
            gap: 3,     // Increase the gap for a more spacious layout
          },
        }}
      >



        <ThemeProvider theme={theme}>
          <Container
            maxWidth="lg"
            sx={{
              mb: 10,
              display: 'flex',
              justifyContent: 'center',  // Centers content horizontally
              alignItems: 'center',      // Centers content vertically
              flexDirection: 'column'    // Ensures that the items are stacked vertically
            }}
          >
            <Grid container spacing={4} justifyContent="center" alignItems={'center'}>


              <Grid
                item
                xs={15}
                sm={10}
                md={8}
                lg={8}    // Custom size for large screens
                xl={10}    // Custom size for extra-large screens
                xxl={10}   // Custom size for even larger screens (if needed)
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>



                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>

                    <Avatar
                      sx={{ width: 150, height: 150, bgcolor: '#0077ff', fontSize: 28 }}
                      src={userData.image || undefined}
                    >
                      {!userData.image && userData.name
                        ? userData.name.charAt(0).toUpperCase()
                        : null}
                    </Avatar>



                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', mt: 2 }}>


                    </Box>



                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', mb: 5 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', mt: 3 }}>
                        <Typography
                          variant="h4"
                          color="white"
                          sx={{
                            fontSize: { xs: '1.5rem', sm: '1.8rem', md: '2rem', lg: '2rem' }, // Adjust font size for different screen sizes
                            display: 'flex',  // Use flexbox to arrange the name and surname in a row
                            alignItems: 'center', // Vertically align the text
                            letterSpacing: 5
                          }}
                        >
                          <span>{userData.name}</span>
                          <span style={{ marginLeft: '10px' }}>{userData.surname}</span>
                        </Typography>

                      </Box>

                      {isEditing && (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            backgroundColor: '#5555',
                            padding: 2,
                            borderRadius: '8px',
                            boxShadow: 3,
                            width: '50%',
                          }}
                        >
                          <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                            <Input
                              type="file"
                              accept="image/*"
                              onChange={handleImageChange}
                              fullWidth
                              sx={{
                                backgroundColor: '#5555',
                                color: 'white',
                                '& input': {
                                  padding: '10px',
                                },
                              }}
                            />
                            <FormHelperText sx={{ color: 'gray', letterSpacing: 2 }}>
                              Max file size: 250 KB
                            </FormHelperText>

                            {error && (
                              <FormHelperText sx={{ color: 'red', mt: 1 }}>
                                {error}
                              </FormHelperText>
                            )}

                            {previewUrl && (
                              <Box mt={2} mb={1}>
                                <img
                                  src={previewUrl}
                                  alt="Preview"
                                  style={{
                                    width: '150px',
                                    height: '150px',
                                    objectFit: 'cover',
                                    borderRadius: '8px',
                                  }}
                                />
                              </Box>
                            )}

                            <Button
                              type="submit"
                              variant="contained"
                              color="primary"
                              sx={{
                                width: '100%',
                                marginTop: '16px',
                                backgroundColor: '#0077ff',
                                letterSpacing: 2,
                                '&:hover': { backgroundColor: '#005bb5' },
                              }}
                            >
                              Update Profile Picture
                            </Button>
                          </form>
                        </Box>

                      )}

                      <Box
                        sx={{
                          position: 'fixed',
                          bottom: 10,
                          right: 60,
                          zIndex: 1500 // ensures it's above most content
                        }}
                      >
                        <IconButton onClick={toggleEditMode} sx={{ color: 'white' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {isEditing ? (
                              <Save sx={{ marginRight: 1 }} />
                            ) : (
                              <Edit sx={{ marginRight: 1 }} />
                            )}
                            <Typography variant="body1" sx={{ letterSpacing: 2 }}>
                              {isEditing ? 'SAVE' : 'EDIT'}
                            </Typography>
                          </Box>
                        </IconButton>
                      </Box>

                    </Box>

                  </Box>

                </Box>




                <VerificationButton userId={userId} />


                <Box
                  id="personal-info"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: 4,
                    letterSpacing: 2,
                    backgroundColor: '#000000',  // Dark background for modern look
                    borderRadius: 3,
                    boxShadow: 5,  // Add shadow for depth
                    maxWidth: '900px', // Limit the width for large screens
                    margin: '0 auto',  // Center on the page
                    marginTop: 10,  // Add top margin for space
                    textAlign: 'center', // Center the text
                    width: '100%',  // Make it responsive
                    border: '1px solid #ddd', // Add border (white in this case)
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <SectionTitle sx={{ color: 'white', marginBottom: 3, letterSpacing: 2 }}>
                      {/* <Person sx={{ fontSize: '2rem', marginRight: 2 }} />  */}
                      Personal Info
                    </SectionTitle>

                    <Grid container spacing={3}>
                      {/* First Name */}
                      <Grid item xs={12} sm={6}>
                        {!isEditing ? (
                          <Typography variant="body1" color="white" sx={{ letterSpacing: 2 }}>
                            First Name: {userData.name}
                          </Typography>
                        ) : (
                          <InputField
                            name="name"
                            value={userData.name}
                            onChange={(e) => handleChange(e, 'name')}
                            label="First Name"
                            variant="outlined"
                            fullWidth
                            color="black"
                            sx={{
                              letterSpacing: 2,
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2, // Rounded corners for input field
                              },
                              '& .MuiInputLabel-root': {
                                color: 'white',  // Black label for better contrast
                              },
                              '& .MuiInputBase-root': {
                                padding: '14px',  // Add padding for spacious input
                              },
                            }}
                          />
                        )}
                      </Grid>

                      {/* Last Name */}
                      <Grid item xs={12} sm={6}>
                        {!isEditing ? (
                          <Typography variant="body1" color="white" sx={{ letterSpacing: 2 }}>
                            Last Name: {userData.surname}
                          </Typography>
                        ) : (
                          <InputField
                            name="surname"
                            value={userData.surname}
                            onChange={(e) => handleChange(e, 'surname')}
                            label="Last Name"
                            variant="outlined"
                            fullWidth
                            color="black"
                            sx={{
                              letterSpacing: 2,
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                              },
                              '& .MuiInputLabel-root': {
                                color: 'white',
                              },
                            }}
                          />
                        )}
                      </Grid>

                      {/* Date of Birth */}
                      <Grid item xs={12} sm={6}>
                        {!isEditing ? (
                          <Typography variant="body1" color="white" sx={{ letterSpacing: 2 }}>
                            Date of Birth: {new Date(userData.dob).toLocaleDateString('en-GB')}
                          </Typography>

                        ) : (
                          <InputField
                            name="dob"
                            value={userData.dob}
                            onChange={(e) => handleChange(e, 'dob')}
                            label="Date of Birth"
                            variant="outlined"
                            type="date"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            sx={{
                              letterSpacing: 2,
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                              },
                              '& .MuiInputLabel-root': {
                                color: 'white',
                              },
                            }}
                          />
                        )}
                      </Grid>

                      {/* Gender */}
                      <Grid item xs={12} sm={6}>
                        {!isEditing ? (
                          <Typography variant="body1" color="white" sx={{ letterSpacing: 2 }}>
                            Gender: {userData.gender}
                          </Typography>
                        ) : (
                          <FormControl fullWidth variant="outlined">
                            <InputLabel
                              id="gender-label"
                              sx={{ color: 'white', '&.Mui-focused': { color: 'white' } }}
                            >
                              Gender
                            </InputLabel>

                            <DarkSelect
                              labelId="gender-label"
                              name="gender"
                              value={userData.gender}
                              onChange={(e) => handleChange(e, 'gender')}
                              label="Gender"
                              variant="outlined"
                              fullWidth
                              color="white"
                              sx={{
                                letterSpacing: 2,
                                color: 'white',
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                },
                                '& .MuiSelect-select': {
                                  color: 'white',
                                },
                              }}
                            >
                              <DarkMenuItem value="Male" sx={{ letterSpacing: 2 }}>
                                Male
                              </DarkMenuItem>
                              <DarkMenuItem value="Female" sx={{ letterSpacing: 2 }}>
                                Female
                              </DarkMenuItem>
                              <DarkMenuItem value="Other" sx={{ letterSpacing: 2 }}>
                                Other
                              </DarkMenuItem>
                            </DarkSelect>
                          </FormControl>
                        )}
                      </Grid>

                    </Grid>
                  </Box>
                </Box>


                <Box

                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: 4,
                    backgroundColor: '#000000',  // Dark background for modern look
                    borderRadius: 3,
                    boxShadow: 5,  // Add shadow for depth
                    maxWidth: '900px', // Limit the width for large screens
                    margin: '0 auto',  // Center on the page
                    marginTop: 10,  // Add top margin for space
                    width: '100%',  // Make it responsive
                    border: '1px solid #ddd',  // Add border (white in this case)
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',  // Ensure the content is stacked vertically
                      alignItems: 'center',  // Center-align the content horizontally
                    }}
                  >
                    <SectionTitle sx={{ color: 'white', marginBottom: 3, letterSpacing: 3 }}>Contact Info</SectionTitle>

                    <Grid container spacing={2} justifyContent="center" alignItems="center">
                      <Grid item xs={12} sm={6}>
                        {!isEditing && (
                          <Typography variant="body1" color="white" sx={{ letterSpacing: 3 }}>
                            <Email sx={{ fontSize: '1.2rem', marginRight: 1, }} /> Email: {userData.email}
                          </Typography>
                        )}
                        {isEditing && (
                          <InputField
                            name="email"
                            value={userData.email}
                            onChange={(e) => handleChange(e, 'email')}
                            label="Email"
                            variant="outlined"
                            fullWidth
                            sx={{
                              letterSpacing: 2,
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                              },
                              '& .MuiInputLabel-root': {
                                color: 'white',
                              },
                              '& .MuiInputBase-input': {
                                letterSpacing: 3, // 👈 Applies spacing to the typed text
                              },
                              '& .MuiInputLabel-root': {
                                letterSpacing: 2, // 👈 Optional: applies spacing to the label
                              },
                            }}
                          />
                        )}
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        {!isEditing && (
                          <Typography variant="body1" color="white" sx={{ letterSpacing: 3 }}>
                            <Phone sx={{ fontSize: '1.2rem', marginRight: 1, }} /> Contact: {userData.contact}
                          </Typography>
                        )}
                        {isEditing && (
                          <InputField
                            name="contact"
                            value={userData.contact}
                            onChange={(e) => handleChange(e, 'contact')}
                            label="Phone Number"
                            variant="outlined"
                            fullWidth
                            sx={{
                              letterSpacing: 2,
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                              },
                              '& .MuiInputLabel-root': {
                                color: 'white',
                              },
                              '& .MuiInputBase-input': {
                                letterSpacing: 3, // 👈 Applies spacing to the typed text
                              },
                              '& .MuiInputLabel-root': {
                                letterSpacing: 2, // 👈 Optional: applies spacing to the label
                              },
                            }}
                          />
                        )}
                      </Grid>
                    </Grid>
                  </Box>
                </Box>




                <Box

                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: 4,
                    backgroundColor: '#000000',  // Dark background for modern look
                    borderRadius: 3,
                    boxShadow: 5,  // Add shadow for depth
                    maxWidth: '900px', // Limit the width for large screens
                    margin: '0 auto',  // Center on the page
                    marginTop: 10,  // Add top margin for space
                    width: '100%',  // Make it responsive
                    border: '1px solid #ddd',  // Add border (white in this case)
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',  // Ensure the content is stacked vertically
                      alignItems: 'center',  // Center-align the content horizontally
                    }}
                  >
                    <SectionTitle sx={{ color: 'white', marginBottom: 3, letterSpacing: 3 }}>Address Details</SectionTitle>

                    <Grid container spacing={2} justifyContent="center" alignItems="center">
                      <Grid item xs={12}>
                        {!isEditing ? (
                          <Typography
                            variant="body1"
                            sx={{
                              color: 'white',  // Default color
                              letterSpacing: 2,
                              fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' }, // Adjust font size for responsiveness
                              lineHeight: { xs: 1.4, sm: 1.6, md: 1.8 }, // Adjust line height for better readability
                              wordBreak: 'break-word', // Ensures long words break correctly on smaller screens
                            }}
                          >
                            Address Line: {userData.address || <span style={{ color: 'gray', letterSpacing: 2 }}>Not available</span>}
                          </Typography>

                        ) : (
                          <>
                            <InputField
                              name="address"
                              value={userData.address}
                              onChange={(e) => handleChange(e, 'address')}
                              label="Address Line"
                              variant="outlined"
                              fullWidth
                              inputProps={{ maxLength: 100 }}
                              helperText={`${userData.address ? userData.address.length : 0}/100 characters`}
                              FormHelperTextProps={{
                                style: {
                                  letterSpacing: 2,
                                  color: (userData.address && userData.address.length > 90) ? 'orange' : 'inherit',
                                },
                              }}
                            />


                          </>
                        )}
                      </Grid>

                      {/* City Field */}
                      <Grid item xs={12} sm={6}>
                        {!isEditing ? (
                          <Typography variant="body1" sx={{ letterSpacing: 2, color: 'white' }}>
                            City: {userData.city || <span style={{ color: 'white', letterSpacing: 2 }}>Not available</span>}
                          </Typography>
                        ) : (
                          <InputField
                            name="city"
                            value={userData.city}
                            onChange={(e) => handleChange(e, 'city')}
                            label="City"
                            variant="outlined"
                            fullWidth
                            sx={{ letterSpacing: 2 }}
                          />
                        )}
                      </Grid>

                      {/* State Field */}
                      <Grid item xs={12} sm={6}>
                        {!isEditing ? (
                          <Typography variant="body1" sx={{ letterSpacing: 2, color: 'white' }}>
                            State: {userData.state || <span style={{ color: 'white', letterSpacing: 2 }}>Not available</span>}
                          </Typography>
                        ) : (
                          <InputField
                            name="state"
                            value={userData.state}
                            onChange={(e) => handleChange(e, 'state')}
                            label="State"
                            variant="outlined"
                            fullWidth
                            sx={{ letterSpacing: 2 }}
                          />
                        )}
                      </Grid>

                      {/* Country Field */}
                      <Grid item xs={12} sm={6}>
                        {!isEditing ? (
                          <Typography variant="body1" sx={{ letterSpacing: 2, color: 'white' }}>
                            Country: {userData.country || <span style={{ color: 'white', letterSpacing: 2 }}>Not available</span>}
                          </Typography>
                        ) : (
                          <InputField
                            name="country"
                            value={userData.country}
                            onChange={(e) => handleChange(e, 'country')}
                            label="Country"
                            variant="outlined"
                            fullWidth
                            sx={{ letterSpacing: 2 }}
                          />
                        )}
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        {!isEditing ? (
                          <Typography variant="body1" sx={{ letterSpacing: 2, color: 'white' }}>
                            Pincode: {userData.pincode || <span style={{ color: 'white' }}>Not available</span>}
                          </Typography>
                        ) : (
                          <InputField
                            name="pincode"
                            value={userData.pincode}
                            onChange={(e) => handleChange(e, 'pincode')}
                            label="Pincode"
                            variant="outlined"
                            fullWidth
                            sx={{ letterSpacing: 2 }}
                          />
                        )}

                      </Grid>
                    </Grid>
                  </Box>
                </Box>



                <Box

                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: 4,
                    backgroundColor: '#000000',  // Dark background for modern look
                    borderRadius: 3,
                    boxShadow: 5,  // Add shadow for depth
                    maxWidth: '900px', // Limit the width for large screens
                    margin: '0 auto',  // Center on the page
                    marginTop: 10,  // Add top margin for space
                    width: '100%',  // Make it responsive
                    border: '1px solid #ddd',  // Add border (white in this case)
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',  // Ensure the content is stacked vertically
                      alignItems: 'center',  // Center-align the content horizontally
                    }}
                  >
                    <SectionTitle sx={{ color: 'white', marginBottom: 3, letterSpacing: 3 }}>Aadhar Number</SectionTitle>

                    <Grid container spacing={2} justifyContent="center" alignItems="center">
                      <Box id="Aadhar-and-Pan"  > <VerificationForm userId={userId} /></Box>
                    </Grid>
                  </Box>
                </Box>


                <Box
                  id="change-password"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: 4,
                    backgroundColor: 'background.paper',
                    boxShadow: 3,
                    borderRadius: 2,

                    maxWidth: '600px',
                    margin: 'auto',
                    border: '1px solid #ddd',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      boxShadow: 6
                    },

                    mt: 10,  // Add margin-top (adjust the value as needed)
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <SectionTitle
                      sx={{
                        textAlign: 'center',
                        marginBottom: 3,
                        fontSize: '1.5rem',
                        // fontWeight: 'bold',
                        color: '#ffff',
                        letterSpacing: 2
                      }}
                    >
                      Change Password
                    </SectionTitle>

                    {/* Current Password Field (only if user has password) */}
                    {userData.password ? (
                      <InputField
                        label="Current Password"
                        type="password"
                        variant="outlined"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        fullWidth
                        sx={{
                          '& .MuiInputBase-input': {
                            letterSpacing: '3px', // 👈 Applies spacing to the typed text
                          },
                          '& .MuiInputLabel-root': {
                            letterSpacing: '2px', // 👈 Optional: applies spacing to the label
                          },
                        }}
                      />
                    ) : (
                      <Typography variant="body2" color="gray" sx={{ textAlign: 'center', marginTop: 2, letterSpacing: 2 }}>
                        Current password is not available.
                      </Typography>
                    )}

                    {/* New Password Field */}
                    <InputField
                      label="New Password"
                      type="password"
                      variant="outlined"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      fullWidth
                      sx={{
                        '& .MuiInputBase-input': {
                          letterSpacing: '3px', // 👈 Applies spacing to the typed text
                        },
                        '& .MuiInputLabel-root': {
                          letterSpacing: '2px', // 👈 Optional: applies spacing to the label
                        },
                      }}
                    />


                    {/* Confirm New Password Field */}
                    <InputField
                      label="Confirm New Password"
                      type="password"
                      variant="outlined"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      fullWidth
                      sx={{
                        '& .MuiInputBase-input': {
                          letterSpacing: '3px', // 👈 Applies spacing to the typed text
                        },
                        '& .MuiInputLabel-root': {
                          letterSpacing: '2px', // 👈 Optional: applies spacing to the label
                        },
                      }}
                    />

                    {/* Change Password Button */}
                    <Button
                      onClick={handleChangePassword}
                      variant="contained"
                      color="primary"
                      fullWidth
                      sx={{
                        marginTop: 3,
                        // fontWeight: 'bold',
                        textTransform: 'none',
                        borderRadius: 1.5,
                        letterSpacing: 2
                      }}
                    >
                      Change Password
                    </Button>
                  </Box>
                </Box>


                {/* Delete Account Card */}
                <Box
                  id="delete-account"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: 4,
                    backgroundColor: 'background.paper',
                    boxShadow: 3,
                    borderRadius: 2,
                    maxWidth: '600px',
                    margin: 'auto',
                    mt: 10,
                    border: '1px solid #ddd',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      boxShadow: 6
                    },
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <SectionTitle
                      sx={{
                        textAlign: 'center',
                        marginBottom: 2,
                        fontSize: '1.5rem',
                        // fontWeight: 'bold',
                        color: 'white',
                        letterSpacing: 2
                      }}
                    >
                      Delete Account
                    </SectionTitle>

                    <Typography variant="body2" color="gray" sx={{ textAlign: 'center', marginBottom: 3, letterSpacing: 2 }}>
                      Deleting your account is permanent and cannot be undone.
                    </Typography>

                    {/* Display helper text if data is unavailable */}
                    {userData?.name ? (
                      <Typography variant="body2" color="#1E90FF" sx={{ textAlign: 'center', marginBottom: 3, letterSpacing: 2 }}>
                        Your account is currently active.
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="gray" sx={{ textAlign: 'center', marginBottom: 3, letterSpacing: 2 }}>
                        Account status is not available.
                      </Typography>
                    )}

                    <Button
                      onClick={handleDeleteAccount}
                      variant="contained"
                      color="error"
                      fullWidth
                      sx={{
                        // fontWeight: 'bold',
                        textTransform: 'none',
                        borderRadius: 1.5,
                        marginTop: 2,
                        letterSpacing: 2,
                        '&:hover': {
                          backgroundColor: '#d32f2f',
                        }
                      }}
                    >
                      Delete Account
                    </Button>
                  </Box>
                </Box>

              </Grid>
            </Grid>
          </Container>
        </ThemeProvider>

        {/* Snackbar for feedback messages */}
        <Snackbar
          open={openSnackbar}
          autoHideDuration={6000}
          onClose={() => setOpenSnackbar(false)}
        >
          <Alert severity="success" sx={{ width: '100%', letterSpacing: 2 }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>

      <Tooltip title="Scroll to Top" placement="top">

        <IconButton
          onClick={scrollToTop}
          sx={{
            position: 'fixed', bottom: 16, right: 16, backgroundColor: 'white', color: 'black', letterSpacing: 2, "&:hover": {
              color: "#1E90FF",  // Icon color on hover
            },
          }}>
          <ArrowUpwardIcon />
        </IconButton></Tooltip>
      <Footer2 />
    </div>

  );
}

export default UserProfile;